import type { TideDay } from './tide-chart.ts';
import type { DetailRow, Vehicle, WireAircraft, WireBus, WireTrain, WireVessel } from './types.ts';

// The base URL and the fetch live in http.ts — this module's private copy of
// getJson was one of the three that diverged (#76): it had no timeout, so a
// backend that hung mid-restart froze a vehicle poll loop for good and, since
// the outage banner requires every feed to be failing, hid the outage too.
import { getJson } from './http.ts';

/** Drops rows whose value is empty, so popups never show blank fields. */
function rows(...pairs: [string, string | number | null | undefined][]): DetailRow[] {
  return pairs
    .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
    .map(([label, v]) => ({ label, value: String(v) }));
}

/**
 * How long ago something last reported, in words — but only once it is old
 * enough to matter. A ship alongside can be quiet for minutes, and saying so is
 * more honest than showing a stationary dot as though it were a live fix.
 */
export function reportAge(recordedAt: number): string {
  if (!recordedAt) return '';
  const seconds = Math.round((Date.now() - recordedAt) / 1000);
  if (seconds < 120) return '';
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  return `${Math.round(seconds / 3600)}h ago`;
}

export async function fetchAircraft(): Promise<Vehicle[]> {
  const { a } = await getJson<{ t: number; a: WireAircraft[] }>('/aircraft');
  return a.map((v) => ({
    id: `air:${v.i}`,
    kind: 'aircraft' as const,
    lat: v.l,
    lon: v.x,
    heading: v.h,
    title: v.c || v.i.toUpperCase(),
    subtitle: [v.y, v.z === null ? '' : `${v.z.toLocaleString()} ft`,
      v.s === null ? '' : `${Math.round(v.s)} kt`].filter(Boolean).join(' · '),
    special: v.r === 1,
    hex: v.i,
    callsign: v.c,
    // Feet on the wire, metres for the 3D view — which is the only consumer,
    // and puts the plane at its height instead of on a rooftop.
    altitudeM: v.z === null ? null : Math.round(v.z * 0.3048),
    details: rows(
      ['Type', v.y],
      ['Altitude', v.z === null ? '' : `${v.z.toLocaleString()} ft`],
      ['Speed', v.s === null ? '' : `${Math.round(v.s)} kt`],
      ['Track', v.h === null ? '' : `${Math.round(v.h)}°`],
    ),
  }));
}

export async function fetchVessels(): Promise<Vehicle[]> {
  const { v: list } = await getJson<{ t: number; v: WireVessel[] }>('/vessels');
  return list.map((v) => {
    const size = v.ln && v.bm ? `${v.ln} m × ${v.bm} m` : '';
    return {
      id: `sea:${v.i}`,
      kind: 'vessels' as const,
      lat: v.l,
      lon: v.x,
      heading: v.h,
      title: v.n || `MMSI ${v.i}`,
      subtitle: [v.ty, v.s === null ? '' : `${v.s.toFixed(1)} kn`].filter(Boolean).join(' · '),
      special: v.f === 1,
      imo: v.imo,
      vesselType: v.ty,
      lengthM: v.ln,
      // Draught, MMSI and callsign dropped per the design handoff — detail for
      // a marine professional, noise for everyone else. IMO stays: it is what
      // the VesselFinder link is built from.
      details: rows(
        ['Type', v.ty],
        ['Size', size],
        ['Flag', v.fl],
        ['Speed', v.s === null ? '' : `${v.s.toFixed(1)} kn`],
        ['Bound for', v.ds],
        ['IMO', v.imo ?? ''],
        ['Last seen', reportAge(v.r)],
      ),
    };
  });
}

/**
 * Ember's whole fleet vs what is inside the Aberdeen box, from the last poll.
 * Surfaced in the legend because an empty coach layer has three very different
 * causes — none nearby, feed down, or everything reporting too long ago — and
 * they are indistinguishable from a bare "0".
 */
export const busStats = { fleet: 0, inBox: 0, stale: 0, ok: false };

export async function fetchBuses(): Promise<Vehicle[]> {
  const { b, fleet, stale } = await getJson<
    { t: number; fleet: number; stale: number; b: WireBus[] }
  >('/buses');
  busStats.fleet = fleet;
  busStats.inBox = b.length;
  busStats.stale = stale;
  busStats.ok = true;
  return b.map((v) => ({
    id: `bus:${v.i}`,
    kind: 'buses' as const,
    lat: v.l,
    lon: v.x,
    heading: v.h,
    title: v.u || v.n || v.o,
    subtitle: [v.o, v.s === null ? '' : `${Math.round(v.s * 3.6)} km/h`]
      .filter(Boolean).join(' · '),
    special: false,
    details: rows(
      ['Operator', v.o],
      ['Route', v.u],
      ['Vehicle', v.n],
      ['Speed', v.s === null ? '' : `${Math.round(v.s * 3.6)} km/h`],
      ['Heading', v.h === null ? '' : `${Math.round(v.h)}°`],
      ['Last seen', reportAge(v.r)],
    ),
  }));
}

/**
 * Why the train layer is empty, which has two very different causes: the
 * geometry has never been baked, or it has and nothing is currently between
 * two anchors. A bare zero cannot tell them apart.
 */
export const trainStats = { ready: false, note: '', count: 0 };

export async function fetchTrains(): Promise<Vehicle[]> {
  const { tr, r, n } = await getJson<
    { t: number; r: boolean; n: string; tr: WireTrain[] }
  >('/trains');
  trainStats.ready = r;
  trainStats.note = n;
  trainStats.count = tr.length;

  return tr.map((v) => ({
    id: `rail:${v.i}`,
    kind: 'trains' as const,
    lat: v.l,
    lon: v.x,
    heading: v.h,
    title: v.d ? `${v.f} → ${v.d}` : v.f,
    subtitle: [v.o, `${v.b[0]}–${v.b[1]}`, v.c ? `${v.c} car` : '']
      .filter(Boolean).join(' · '),
    // Trains that are running to an estimate rather than a reported time are
    // the weaker half of an already-inferred layer.
    special: v.ob === 0,
    carriages: v.c,
    details: rows(
      ['Operator', v.o],
      ['From', v.f],
      ['To', v.d],
      ['Between', `${v.b[0]} and ${v.b[1]}`],
      ['Along that leg', `${Math.round(v.p * 100)}%`],
      ['Formation', v.c ? `${v.c} ${v.c === 1 ? 'carriage' : 'carriages'}` : ''],
      // The reason this field exists: at Portlethen only the front two
      // carriages reach the platform, and someone sitting in the fifth needs
      // to move before the doors open, not after.
      ['⚠ Doors', v.w],
      // The honest part. This is not a GPS fix and the popup has to say so
      // rather than leaving the dot to imply one.
      ['Position', v.ob
        ? 'Estimated from the last reported time'
        : 'Estimated from a predicted time'],
      ['Based on a report', v.ag < 90 ? 'just now' : `${Math.round(v.ag / 60)} min ago`],
    ),
  }));
}

/**
 * Every bus stop in the place. Names and coordinates only — the backend holds
 * 688,000 departures behind /stops/:id and none of them belong here.
 */
export function fetchStops(): Promise<{
  ready: boolean; note: string;
  s: { id: string; name: string; lat: number; lon: number; t?: 1; s?: 1; f?: 1 }[];
}> {
  return getJson('/stops');
}

export interface BusRoute {
  id: string;
  name: string;
  longName: string;
  operator: string;
  colour: string;
  lines: [number, number][][];
}

/** One route's geometry, fetched when someone clicks it. About 4 kB. */
export function fetchRoute(id: string): Promise<BusRoute> {
  return getJson<BusRoute>(`/routes/${encodeURIComponent(id)}`);
}

/** A route as the picker knows it: a name, an operator, no geometry. */
export interface RouteName {
  id: string;
  name: string;
  operator: string;
}

/**
 * Every route that has a line to draw — names only.
 *
 * The whole list is about 35 kB against 4 kB for one route's geometry, which
 * is the trade the endpoint was built for: a search box needs to know that the
 * X8 exists before anybody has clicked it, and does not need to know where it
 * goes until they do.
 *
 * Only routes with geometry, which is the backend's own rule for this list and
 * exactly the right one here — offering a search hit that draws nothing is the
 * dead row that #39 spent a release removing from the boards.
 */
export function fetchRoutes(): Promise<{ ready: boolean; r: RouteName[] }> {
  return getJson<{ ready: boolean; r: RouteName[] }>('/routes');
}

export type RiverWindow = '4h' | '24h' | '7d';

export interface RiverHistory {
  id: string;
  station: string;
  unit: string;
  window: RiverWindow;
  /** [epoch SECONDS, value] — seconds, so a week of readings stays small. */
  points: [number, number][];
  min: number | null;
  max: number | null;
}

/**
 * A river gauge's recent levels, fetched when its card is opened.
 *
 * On demand rather than with the feature list: a week is 672 readings per
 * gauge and most gauges are never clicked. The backend caches for 15 minutes,
 * which is how often SEPA produce a new reading.
 */
export function fetchRiverHistory(tsId: string, window: RiverWindow): Promise<RiverHistory> {
  return getJson<RiverHistory>(`/rivers/${encodeURIComponent(tsId)}/history?window=${window}`);
}

export interface AircraftDetail {
  hex: string;
  registration: string;
  typeName: string;
  manufacturer: string;
  owner: string;
  photoUrl: string;
  photoLink: string;
  photographer: string;
  origin: string;
  destination: string;
}

/** Photo, registration and route — fetched only when a popup opens. */
/** A day of tidal events for one station, for the curve on its card. */
export function fetchTideDay(stationId: string): Promise<TideDay> {
  return getJson<TideDay>(`/tides/${encodeURIComponent(stationId)}/day`);
}

export async function fetchAircraftDetail(hex: string, callsign: string): Promise<AircraftDetail> {
  const q = callsign ? `?callsign=${encodeURIComponent(callsign)}` : '';
  return getJson<AircraftDetail>(`/aircraft/${encodeURIComponent(hex)}/detail${q}`);
}
